
import React, { useEffect } from "react";
import { Users } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface FamilyMembersCardProps {
  memorialId: string;
}

export const FamilyMembersCard: React.FC<FamilyMembersCardProps> = ({ memorialId }) => {
  const queryClient = useQueryClient();

  // Set up real-time subscription for family members
  useEffect(() => {
    if (!memorialId) return;

    const channel = supabase
      .channel('family_members_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'family_members',
        filter: `memorial_id=eq.${memorialId}`
      }, (payload) => {
        // Invalidate and refetch family members when changes occur
        queryClient.invalidateQueries({ queryKey: ['familyMembers', memorialId] });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [memorialId, queryClient]);

  // Fetch family members with improved error handling
  const { data: familyMembers = [], isLoading: isFamilyLoading, isError } = useQuery({
    queryKey: ['familyMembers', memorialId],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('family_members')
          .select('*')
          .eq('memorial_id', memorialId)
          .order('created_at', { ascending: true });

        if (error) {
          console.error('[FamilyMembersCard] Error fetching family members:', error);
          throw error;
        }

        return data || [];
      } catch (error) {
        console.error('[FamilyMembersCard] Exception in family members fetch:', error);
        throw error;
      }
    },
    enabled: !!memorialId,
    staleTime: 0, // Always refetch when queryClient.invalidateQueries is called
    refetchOnWindowFocus: true,
    refetchOnMount: true
  });

  return (
    <div className="bg-card rounded-xl shadow-sm border overflow-hidden transition-all duration-300 hover:shadow-md">
      <div className="border-b bg-muted/30 px-6 py-4 flex items-center">
        <Users className="h-5 w-5 text-primary mr-2" />
        <h3 className="font-medium text-foreground">Family</h3>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {isFamilyLoading ? (
            <p className="text-sm text-muted-foreground">Loading family members...</p>
          ) : isError ? (
            <p className="text-sm text-destructive">Failed to load family members. Please refresh the page.</p>
          ) : familyMembers.length > 0 ? (
            <div className="divide-y">
              {familyMembers.map((member) => (
                <div key={member.id} className="py-3 first:pt-0 last:pb-0">
                  <p className="text-foreground font-medium">{member.name}</p>
                  <p className="text-sm text-muted-foreground">{member.relation}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No family members added yet</p>
          )}
        </div>
      </div>
    </div>
  );
};
